import { create } from "zustand"
import { useMemo, useRef, FC, useEffect } from 'react'
import { Modal } from 'bootstrap'
import { Formik, FormikProps, FormikValues, Form, getIn } from 'formik'
import { SubmitButtonRow } from '../layout'
import { StatusErrorMessage } from '../widgets/StatusErrorMessage'
import * as Yup from 'yup'
import { useQuery } from 'react-query'
import { getProductTypes, ProductCategory, Scheme, ProductType } from '../../modules/core/products'
import { filter, findIndex, sortBy } from 'lodash'
import { FormikSelect } from '../widgets/CustomFormikFormFields'

type EditableProductDialogModel = {
  productTypeId: number
  productTypeString: string
  schemeId: number
  schemeString: string
  productCategoryId: number
  productCategoryString: string
}

export type EditableProductDialogRef = {
  show: Function
  hide: Function
}

export type EditableProductDetails = {
  productType: ProductType
  scheme: Scheme
  productCategory: ProductCategory
}

const EditableProductDialogSchema: any = Yup.object({
  productTypeId: Yup.number().required('Select Product Type'),
  schemeId: Yup.number().required('Select Scheme'),
  productCategoryId: Yup.number().required('Select Product Category'),
})

type SelectOptions = {
  value: string
  text: string
}

interface EditableProductModalState { 
  show: boolean
  details: EditableProductDetails | null
  onSave?: (values: EditableProductDialogModel) => void
  toggleShow: (show: boolean) => void
  setDetails: (details: EditableProductDetails) => void
  setOnSave: (onSave: (values: EditableProductDialogModel) => void) => void
  clear: () => void,
}

export const useEditableProductModalState = create<EditableProductModalState>()((set, get) => ({
  show: false,
  details: null,
  onSave: undefined,
  toggleShow: (show: boolean) => set({show}),
  setDetails: (details: EditableProductDetails) => set({details}),
  setOnSave: (onSave: (values: EditableProductDialogModel) => void) => set({onSave}),    
  clear: () => set({ 
    show: false,
    details: null,
    onSave: undefined,
  }),
}));


type FormProps = { 
  details: EditableProductDetails
  doClose: Function
}

const EditProductDialogForm: FC<FormikProps<EditableProductDialogModel> & FormProps> = ({ details, doClose, status, setFieldValue, values }) => {

  const productTypeID = parseInt(`0${getIn(values, 'productTypeId')}`);
  
  const { isFetching, data: productTypesResponse } = useQuery(
    `GETPRODUCTTYPES`,
    () => { return getProductTypes() },
    {
      keepPreviousData: true, refetchOnWindowFocus: false,
      staleTime: 600000,   // keep fresh for 10 mins (no refetch)
      cacheTime: 600000,   // clear from cache after 10 mins to free mem
    }
  )

  const productTypes = useMemo(() => {

    let productTypes: Array<ProductType> = (isFetching || (! (productTypesResponse && productTypesResponse?.data && Array.isArray(productTypesResponse?.data)))) ? [] :  productTypesResponse.data;

    if (details && details?.productType && details.productType?.id && findIndex(productTypes, {id: details.productType.id}) === -1) {
      // @ts-ignore
      productTypes.push({ id: productType.id, name: productType.name, schemes: [], productCategoriesSet: []});
    }

    return productTypes;

  }, [isFetching, productTypesResponse, details]);


  const { schemeOptions, productCategoryOptions } = useMemo(() => {

    let schemeOptions: Array<SelectOptions> = [];
    let productCategoryOptions: Array<SelectOptions> = []

    if (productTypes.length === 0) {
      if (details && details?.scheme) {
        schemeOptions.push({ value: `${details.scheme.id}`, text: details.scheme.name});
      }
      if (details && details?.productCategory) {
        productCategoryOptions.push({ value: `${details.productCategory.id}`, text: details.productCategory.name})
      }
      return {schemeOptions, productCategoryOptions};
    }

    let _idx = findIndex(productTypes, { id: productTypeID} );
    if (_idx === -1) {
      return {schemeOptions, productCategoryOptions};
    }

    const { schemes, productCategoriesSet } = productTypes[_idx];

    // repopulate schemes/product categories from details
    schemeOptions = sortBy(filter(schemes, (dat) => (! dat?.deleted)),['name']).map( dat => ({value: `${dat.id}`, text: dat.name}));
    productCategoryOptions = sortBy(filter(productCategoriesSet, (dat) => (! dat?.deleted)),['name']).map( dat => ({ value: `${dat.id}`, text: dat.name}));

    // move Others to end
    let _idx_others = findIndex(schemeOptions, { text: 'Others'});
    if (_idx_others > -1) {
      schemeOptions.push(schemeOptions[_idx_others]);
      schemeOptions.splice(_idx_others, 1);
    }

    _idx_others = findIndex(productCategoryOptions, { text: 'Others'});
    if (_idx_others > -1) {
      productCategoryOptions.push(productCategoryOptions[_idx_others]);
      productCategoryOptions.splice(_idx_others, 1);
    }

    return {schemeOptions, productCategoryOptions};

  }, [productTypes, productTypeID, details]);


  return(
  <Form id='kt_application_edit_product_dialog_form' className='form' noValidate>
    <div className='' >
      <div className="row mb-1">
        <FormikSelect size="12" label="Product Type" name="productTypeId" required={true} placeholder='Select Product Type'
          options={sortBy(filter(productTypes, (dat: any) => (! dat?.deleted)),['name']).map( dat => ({value: `${dat.id}`, text: dat.name}))}
          onChange={(e: any) => {
            setFieldValue('schemeId', '');
            setFieldValue('schemeString', '');
            setFieldValue('productCategoryId', '');
            setFieldValue('productCategoryString', '');
            setFieldValue('productTypeId', parseInt(`${e.target.value}`), true);
            setFieldValue('productTypeString', e.target.options[e.target.selectedIndex].text, true)
          }}
        />
      </div>
      <div className="row mb-1">
        <FormikSelect size="12" label="Product Scheme" name="schemeId" required={true} options={schemeOptions} placeholder='Select Scheme' disabled={! (productTypeID && productTypeID > 0)}
          onChange={(e: any) => {
            setFieldValue('schemeId', parseInt(`${e.target.value}`), true);
            setFieldValue('schemeString', e.target.options[e.target.selectedIndex].text, true);
          }}
        />
      </div>
      <div className="row mb-1">            
        <FormikSelect size="12" label="Product Category" name="productCategoryId" required={true} options={productCategoryOptions} placeholder='Select Category' disabled={! (productTypeID && productTypeID > 0)}
          onChange={(e: any) => {
            setFieldValue('productCategoryId', parseInt(`${e.target.value}`), true);
            setFieldValue('productCategoryString', e.target.options[e.target.selectedIndex].text, true)
          }}
        />
      </div>
      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={() => doClose()} cancelText="Cancel" submitText="Save" />
    </div>
  </Form>
  )
}


export const EditableProductDialog = () => {

  const { show, details, onSave, toggleShow } = useEditableProductModalState(state => state);

  const { productType, scheme, productCategory } = details ?? {};

  const modalRef = useRef<HTMLDivElement | null>(null);
  
  const initValues = useMemo(() => {

    return {
      productTypeId: productType?.id ?? 0,
      productTypeString: productType?.name ?? '',
      schemeId: scheme?.id ?? 0,
      schemeString: scheme?.name ?? '',
      productCategoryId:  productCategory?.id ?? 0,
      productCategoryString:  productCategory?.name ?? '',
    }

  }, [productType, scheme, productCategory]);



  const doSubmit = (values: any, actions: FormikValues) => {
    const { setStatus, setSubmitting } = actions;
    setStatus('');
    
    if (onSave) onSave(values);
    toggleShow(false);
    setSubmitting(false);
  }


  useEffect(() => {
    if (! (modalRef && modalRef.current)) return;

    const modalEle = modalRef.current;    
    if (show) {
      const bsModal = new Modal(modalEle, {
        backdrop: false,
        keyboard: false
      })
      bsModal.show();
      return;                
    } 
    const bsModal= Modal.getInstance(modalEle)
    if (bsModal) bsModal.hide();       
  
  // eslint-disable-next-line          
  }, [show, modalRef]);
  
  return (
  <div className="modal" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="staticBackdropLabel" aria-hidden="true">
    <div className="modal-dialog modal-dialog-centered mw-400px">
      <div className="modal-content ">
        <div className="modal-header">
          <h1 className="modal-title fs-5" id="staticBackdropLabel">Edit Product</h1>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => toggleShow(false)}></button>
        </div>
        <div className="modal-body">
          <Formik
            initialValues={initValues}
            validationSchema={EditableProductDialogSchema}
            enableReinitialize={true}
            onSubmit={doSubmit}
            component={(props: any) => <EditProductDialogForm details={details} doClose={() => toggleShow(false)} {...props} />  }
          />
        </div>
      </div>
    </div>
  </div>
  )
};